import React, {useMemo, useState, useCallback} from 'react'
import {
  Box,
  Grid,
  Link,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
} from '@mui/material'
import {fonts, theme} from '../../../theme/DashboardTheme'
import {BrokerOptOuts} from 'shared/redux/peopleFinder/PeopleFinderTypes'
import {Grant} from "shared/redux/product/ProductTypes";
import Loader from "../atom/Loader";

export interface OptoutsProp {
  grant: Grant | undefined
  optouts: BrokerOptOuts[]
  isLoading: boolean
}

const OptOuts = ({grant, optouts, isLoading}: OptoutsProp) => {
  const shouldNotRouteToSubscriptionPage =grant && grant.grant_source === 'family_grant'
  const [page, setPage] = useState(0)
  const rowsPerPage = 10
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const shouldDisplayUpgrade=useCallback((status: string, upgrade_required: boolean)=>{
    if(!grant) return true
    if(status==='pending' && upgrade_required) return true
    return false
  },[grant])

  const sortedRows = React.useMemo(() => {
      if(!grant) return optouts

      const groupRows: BrokerOptOuts[] = [];
      const upgradeRows: BrokerOptOuts[] = [];

      optouts.forEach((row: BrokerOptOuts) => {
        if (shouldDisplayUpgrade(row.status, row.upgrade_required)) {
          upgradeRows.push(row);
        } else {
          groupRows.push(row);
        }
      })

      const result: BrokerOptOuts[] = [];
      let groupIndex = 0;
      let upgradeIndex = 0;

      while (groupIndex < groupRows.length || upgradeIndex < upgradeRows.length) {
        for (let i = 0; i < 4 && groupIndex < groupRows.length; i++) {
          result.push(groupRows[groupIndex++]);
        }

        if (upgradeIndex < upgradeRows.length) {
          result.push(upgradeRows[upgradeIndex++])
        }
      }

      return result
    }, [optouts, shouldDisplayUpgrade, grant]);

  const paginatedRows = useMemo(
    () => sortedRows.slice(page * rowsPerPage, (page + 1) * rowsPerPage),
    [sortedRows, page],
  )

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage - 1)
  }

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })

  const columns = useMemo(
    () => [
      {key: 'broker_name', name: 'Broker', width: 150},
      {key: 'updated_at', name: 'Opt Out Submitted At', width: 150},
      {key: 'status', name: 'Status', width: 150},
    ],
    [],
  )

  const getStatusStyles = (status: string) => {
    const statusMap: Record<string, { color: string; text: string }> = {
      complete: { color: '#4CAF50', text: 'Opt Out Submitted' },
      optout_submitted: { color: '#4CAF50', text: 'Opt Out Submitted' },
      removed: { color: '#4CAF50', text: 'Removed' },
      pending: { color: '#FFC107', text: 'Opt Out Pending' },
      failed: { color: '#FFC107', text: 'Opt Out Pending' },
    }
    return statusMap[status] || {color: '#000000', text: status}
  }

  const BoxShadow = '0px 4px 10px rgba(208, 210, 218, 0.37)'
  return (<Loader loading={isLoading}>
      {optouts && optouts.length === 0 ? (
        <Box
          className='opt-outs-table'
          sx={{
            p: 4,
            textAlign: 'center',
            bgcolor: '#fff',
            borderRadius: 5,
            boxShadow: BoxShadow,
            mt: 5,
            mb: 2,
            height: 300,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Typography
            variant="body1"
            sx={{
              color: '#666',
              fontWeight: 500,
              fontSize: '1.1rem',
              ...fonts.Inter.Medium
            }}
          >
            Please wait, your opt-out requests are currently being processed
          </Typography>
        </Box>
      ) : !isMobile ? (
        <Box
          sx={{
            width: '100%',
            pb: 2,
            mt: 5,
            bgcolor: '#fff',
            borderRadius: 5,
            boxShadow: BoxShadow,
            p: 5,
            mb: '20px'
          }}>
          <TableContainer className='opt-outs-table'>
            <Table sx={{bgcolor: '#fff', borderRadius: 2, tableLayout: 'fixed'}}>
              <TableHead>
                <TableRow>
                  {columns.map((column) => (
                    <TableCell
                      key={column.key}
                      sx={{
                        borderBottom: '1px solid #9135E0',
                        padding: '16px 10px 10px 1px',
                        ...fonts.Inter.SemiBold,
                        fontWeight: 600,
                        textAlign: 'left',
                      }}>
                      {column.name}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {paginatedRows.map((row, index) => (
                  <TableRow key={index}>
                    {columns.map((column) => {
                      const value = row[column.key as keyof BrokerOptOuts]
                      return (
                        <TableCell
                          key={column.key}
                          sx={{
                            borderBottom: '1px solid rgba(97, 95, 107, 0.5)',
                            textAlign: 'left',
                            padding: '16px 10px 10px 1px',
                            fontFamily: 'InterMedium',
                            fontWeight: 500,
                            width: column.width,
                          }}>
                          {column.key === 'status' ? (
                            !shouldDisplayUpgrade(row.status, row.upgrade_required) ? (
                              <Typography
                                sx={{
                                  color: getStatusStyles(value as string)
                                    .color,
                                }}>
                                {getStatusStyles(value as string).text}
                              </Typography>
                            ) : (
                              shouldNotRouteToSubscriptionPage?
                              <Typography sx={{fontFamily:"InterMedium", color:'#426BF0', fontSize:14 }}>
                                  Upgrade to Opt Out
                              </Typography>
                              :
                              <Link href="/subscription" underline="none">
                                <Typography sx={{ textDecoration: 'underline', fontFamily:"InterMedium", color:'#426BF0', fontSize:14 }}>
                                  {!grant ? 'Subscribe to Opt Out' : 'Upgrade to Opt Out'}
                                </Typography>
                              </Link>
                            )
                          ) : column.key === 'updated_at' ? (
                            !shouldDisplayUpgrade(row.status, row.upgrade_required) ? (
                              formatDate(value as string)
                            ) : (
                              <Typography
                                sx={{
                                  color: 'transparent',
                                  textShadow: '0 0 8px rgba(0,0,0,0.5)',
                                  userSelect: 'none',
                                }}>
                                Blurred Content
                              </Typography>
                            )
                          ) : (
                            value
                          )}
                        </TableCell>
                      )
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                mt: 2,
                p: 2,
                borderRadius: 2,
                bgcolor: '#fff',
              }}>
              <Pagination
                variant="outlined"
                shape="rounded"
                count={Math.ceil(optouts.length / rowsPerPage)}
                page={page + 1}
                onChange={handleChangePage}
                sx={{
                    '& .MuiPaginationItem-root': {
                      fontSize: '14px',
                      fontWeight: 600,
                      fontFamily: 'InterSemiBold',
                    },
                    '& .MuiPaginationItem-root.Mui-selected': {
                      backgroundColor: 'transparent',
                      color: '#426BF0',
                      fontWeight: 600,
                      fontSize: '14px',
                      fontFamily: 'InterSemiBold',
                      border: '1px solid #426BF0',
                    },
                      '& .MuiPaginationItem-previousNext.Mui-disabled': {
                      backgroundColor: '#c8ced5',
                    },
                }}
              />
            </Box>
          </TableContainer>
        </Box>
      ) : (
        <Box sx={{width: '100%', mb:'20px', bgcolor: '#fff', borderRadius: 2, p: 3, boxShadow: BoxShadow}}>
          <Typography variant="h5" mx={2} mt={2} mb={3}>
            Opt Outs ({optouts.length})
          </Typography>

          {paginatedRows.map((row, index) => (
            <Grid
              key={index}
              container
              spacing={2}
              sx={{borderBottom: '1px solid #615F6B', p: 2}}>
              {columns.map((col) => {
                const value = row[col.key as keyof BrokerOptOuts]
                return (
                  <Grid item key={col.key} xs={6}>
                    <Box sx={{display: 'flex', flexDirection: 'column'}}>
                      <Typography variant="subtitle2">{col.name}:</Typography>

                      {col.key === 'status' ? (
                        !shouldDisplayUpgrade(row.status, row.upgrade_required) ? (
                          <Typography
                            sx={{
                              color: getStatusStyles(value as string).color,
                            }}>
                            {getStatusStyles(value as string).text}
                          </Typography>
                        ) : (
                          shouldNotRouteToSubscriptionPage?
                          <Typography sx={{fontFamily:"InterMedium", color:'#426BF0', fontSize:14 }}>
                              Upgrade to Opt Out
                          </Typography>
                          :
                          <Link href="/subscription" underline="none">
                            <Typography sx={{ textDecoration: 'underline', fontFamily:"InterMedium", color:'#426BF0', fontSize:14 }}>
                              {!grant ? 'Subscribe to Opt Out' : 'Upgrade to Opt Out'}
                            </Typography>
                          </Link>
                        )
                      ) : col.key === 'updated_at' ? (
                        !shouldDisplayUpgrade(row.status, row.upgrade_required) ? (
                          <Typography>
                            {formatDate(value as string)}
                          </Typography>
                        ) : (
                          <Typography
                            sx={{
                              color: 'transparent',
                              textShadow: '0 0 8px rgba(0,0,0,0.5)',
                              userSelect: 'none',
                            }}>
                            Blurred Content
                          </Typography>
                        )
                      ) : (
                        <Typography>{value}</Typography>
                      )}
                    </Box>
                  </Grid>
                )
              })}
            </Grid>
          ))}
        </Box>
      )}
    </Loader>
  )
}

export default OptOuts
